import { Box, Button, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { lighten, useTheme } from '@mui/material/styles';
import LineClampTooltip from 'components/common/LineClampTooltip';
import OmmnioDataGrid from 'components/common/OmmnioDataGrid';
import deferRender from 'defer-render-hoc';
import isObject from 'lodash/isObject';
import CheckCircleIcon from 'mdi-react/CheckCircleIcon';
import HelpCircleOutlineIcon from 'mdi-react/HelpCircleOutlineIcon';
import { func, object, string } from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { I18n } from 'utils/i18n';
import { readXlsxFromFile, readXlsxFromFileData, toJson } from 'utils/xlsxUtils';

const getStyles = theme => ({
  root: {
    flexGrow: 1,
    overflowY: 'auto',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    p: '20px',
    '& .import-preview-tabs': {
      mt: 2,
    },
  },
  grid: {
    mt: 2,
    mb: 2,
    flex: 1,
    minHeight: 0,
  },
  colHeader: { display: 'flex', flexDirection: 'row', alignItems: 'center' },
  check: {
    width: 14,
    color: theme.palette.ok,
  },
  notFound: {
    width: 14,
    color: theme.palette.danger,
  },
  iconWrapper: {
    display: 'flex',
  },
  'status-created': {
    color: lighten(theme.palette.success.main, 0.8),
  },
  'status-updated': {
    color: lighten(theme.palette.info.main, 0.8),
  },
  'status-skipped': {
    color: lighten(theme.palette.error.main, 0.8),
  },
  'status-error': {
    color: lighten(theme.palette.error.main, 0.8),
  },
  tabTitle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    '& span + span': {
      ml: 2,
    },
  },
  actions: {
    mt: 2,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    '& button + button': {
      ml: 2,
    },
  },
});

const statusSx = (styles, status) => styles[`status-${status}`] || {};

const renderHeader = (fieldName, styles) => params => {
  let Icon = () => null;
  if (params.field !== '_message') {
    Icon = fieldName ? CheckCircleIcon : HelpCircleOutlineIcon;
  }
  const iconSx = fieldName ? styles.check : styles.notFound;
  return (
    <Box sx={styles.colHeader}>
      <Tooltip
        title={I18n.t(fieldName ? 'ImportsScene.FieldFound' : 'ImportsScene.FieldNotFound', {
          name: params.field,
        })}
      >
        <Box sx={styles.iconWrapper}>
          <Icon style={iconSx} />
        </Box>
      </Tooltip>
      &nbsp;
      <Typography variant="tableHeader">{params.colDef.headerName}</Typography>
    </Box>
  );
};

const ImportPreview = ({
  importPreview = null,
  file = null,
  onCancel,
  downloadResumedImport,
  fileData = null,
  onStart,
  resultStatus = null,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [data, setData] = useState(null);
  const [, setFailed] = useState(null);
  const [sortModel, setSortModel] = useState([{ field: '_status', sort: 'asc' }]);
  const [status, setStatus] = useState(resultStatus || Object.keys(importPreview?.data || {})[0]);
  const [statuses, setStatuses] = useState([]);

  const isResult = !!resultStatus;

  const processWb = useCallback((XLSX, wb) => {
    const sheet = wb.SheetNames[0];
    const res = toJson(XLSX, wb);
    const newData = res[sheet];
    setData(newData);
  }, []);

  const downloadUrl = importPreview?.downloadUrl || importPreview?.employeeImport?.httpLink;

  useEffect(() => {
    new Promise(resolve => {
      if (file) {
        resolve(readXlsxFromFile(file));
      } else if (!fileData && downloadUrl) {
        downloadResumedImport(downloadUrl);
        resolve({});
      } else if (fileData) {
        resolve(readXlsxFromFileData(fileData));
      }
      return {};
    })
      .then(({ XLSX, wb }) => wb && processWb(XLSX, wb))
      .catch(error => {
        console.log(error);
        setFailed({
          type: 'failed',
          error,
        });
      });
  }, [downloadResumedImport, file, fileData, downloadUrl, processWb]);

  useEffect(() => {
    if (importPreview?.data) {
      const dataKeys = Object.keys(importPreview.data);
      const newStatuses = [];
      Object.entries(importPreview.data).forEach(([_status, indexes]) => {
        indexes.forEach(idx => {
          const { message: _message, index } = isObject(idx) ? idx : { index: idx };
          newStatuses[index] = {
            _status,
            _message,
          };
        });
      });
      setStatuses(newStatuses);
      setStatus(prev => prev || dataKeys[0]);
    }
  }, [importPreview?.data]);

  const rows = data
    ?.map((d, idx) => ({
      _key: `${status}-${idx}`,
      ...statuses[idx],
      ...d,
    }))
    .filter((d, idx) => statuses[idx]?._status === status);

  const values = { created: 2, updated: 1, skipped: 0, error: 0 };
  const showMessage = ['skipped', 'error'].includes(status);
  const importHeaders = importPreview?.employeeImport?.headers;
  const columns = importHeaders
    ? [...(showMessage ? ['_message'] : []), ...importHeaders].map((field, idx) => {
        const fieldName = importPreview.employeeImport.headerMap[idx - (showMessage ? 1 : 0)];
        let headerName = fieldName ? I18n.t(`ImportsScene.Fields.${fieldName}`) : `${field}`;
        if (['_status', '_message', '_id'].includes(field)) {
          headerName = I18n.t(`ImportsScene.Fields.${field}`);
        }
        return {
          field,
          ...(field === '_status' ? { sortComparator: (a, b) => values[a] - values[b] } : {}),
          headerName,
          editable: false,
          minWidth: field === '_message' ? 300 : 150,
          type: field === 'trackTime' ? 'boolean' : 'string',
          ...(field === 'trackTime'
            ? {
                type: 'boolean',
                valueGetter: value =>
                  ['true', '1', 'si', 'sí', 'cierto', 'cert', 'yes'].includes(
                    `${value}`.toLowerCase().trim(),
                  ),
              }
            : {
                renderCell: params => (
                  <LineClampTooltip
                    lines={params.value?.indexOf && params.value?.indexOf(' ') > -1 ? 2 : 1}
                  >
                    {params.formattedValue}
                  </LineClampTooltip>
                ),
              }),
          ...({
            _status: {
              renderCell: params => (
                <Box sx={statusSx(styles, params.value)}>
                  {I18n.t(`ImportsScene.Statuses.${params.value}`)}
                </Box>
              ),
            },
          }[field] || {}),
          renderHeader: renderHeader(fieldName, styles),
        };
      })
    : null;
  const handleChangeStatus = (e, val) => setStatus(val);
  const handleCancel = () => onCancel(importPreview?.employeeImport);
  const handleStart = () => onStart(importPreview?.employeeImport);
  return (
    <Box sx={styles.root}>
      <Typography component="div" variant="h5">
        {isResult ? I18n.t(`ImportsScene.Import results`) : I18n.t(`ImportsScene.Import preview`)}
      </Typography>
      <Tabs value={status} onChange={handleChangeStatus} indicatorColor="primary" centered>
        {Object.entries(importPreview?.data || {}).map(([k, v]) => (
          <Tab
            className="import-preview-tabs"
            key={k}
            label={
              <Box sx={styles.tabTitle}>
                <Typography variant="button">
                  {I18n.t(`ImportsScene.${isResult ? 'ResultStatuses' : 'Statuses'}.${k}`, {
                    count: v.length,
                  })}
                </Typography>
                <Typography variant="caption">{v.length}</Typography>
              </Box>
            }
            value={k}
          />
        ))}
      </Tabs>
      {columns && rows && (
        <OmmnioDataGrid
          sx={styles.grid}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          getRowId={m => m._key}
          columns={columns}
          rows={rows}
        />
      )}
      {!isResult && (
        <Box sx={styles.actions}>
          <Button variant="outlined" type="button" color="secondary" onClick={handleCancel}>
            {I18n.t('ImportsScene.Cancel import')}
          </Button>
          <Button variant="contained" type="button" color="primary" onClick={handleStart}>
            {I18n.t('ImportsScene.Start import')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

ImportPreview.propTypes = {
  downloadResumedImport: func.isRequired,
  importPreview: object,
  file: object,
  onCancel: func.isRequired,
  onStart: func.isRequired,
  fileData: object,
  resultStatus: string,
};

export default deferRender(ImportPreview);
